import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Badge,
  Progress,
  HStack,
  VStack,
  useColorModeValue,
  IconButton,
  Tooltip,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useToast,
} from "@chakra-ui/react";
import { RepeatIcon, InfoIcon } from "@chakra-ui/icons";
import useAxios from "../utils/useAxios";
import Navbar from "../components/Navbar";

type ServiceStatus = {
  status: string;
  error?: string;
  workers?: string[];
};

type HealthResponse = {
  app: string;
  db: ServiceStatus;
  celery: ServiceStatus;
};

type SystemResponse = {
  cpu: {
    percent: number;
    cores: number;
  };
  memory: {
    total: number;
    available: number;
    used: number;
    percent: number;
  };
  loadavg: {
    short: number;
    medium: number;
    long: number;
  } | null;
  disk: {
    total: number;
    used: number;
    free: number;
    percent: number;
  } | null;
  services: {
    db: ServiceStatus;
    celery: ServiceStatus;
  };
};

type Gateway = {
  gateway_name: string | null;
  gateway_address: string;
  last_seen: string;
  number_of_devices: number;
  number_of_messages_last_hour: number;
  id: number;
  gateway_battery_voltage: number | null;
  label_code: string | null;
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  accuracy: number | null;
  altitude_accuracy: number | null;
  organization_name: string | null;
};

const UPDATE_TIME = 5000;
const GATEWAY_UPDATE_TIME = 15000; // Update gateways every 15 seconds

const statusColor = (s: ServiceStatus | string) => {
  const val = typeof s === "string" ? s : s.status;
  if (val === "ok") return "green";
  if (val === "not_configured" || val === "unknown") return "yellow";
  return "red";
};

const formatBytes = (bytes?: number) => {
  if (!bytes && bytes !== 0) return "-";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const safeFixed = (val: number | undefined | null, digits = 1) =>
  typeof val === "number" ? val.toFixed(digits) : "-";

const isGatewayOnline = (gateway: Gateway): boolean => {
  // Consider a gateway online if it has sent messages in the last hour
  return gateway.number_of_messages_last_hour > 0;
};

const isGatewayDown = (gateway: Gateway): boolean => {
  // Gateway is down if it has an organization name, was active in the last 30 days, but has no messages in the last hour
  if (!gateway.organization_name) return false;

  const lastSeenDate = new Date(gateway.last_seen);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const wasActiveRecently = lastSeenDate >= thirtyDaysAgo;
  const hasNoRecentMessages = gateway.number_of_messages_last_hour === 0;

  return wasActiveRecently && hasNoRecentMessages;
};

const getBatteryPercentage = (voltage: number | null): number | null => {
  if (!voltage) return null;
  // Assuming typical Li-ion battery: 3.2V (0%) to 4.2V (100%)
  const minVoltage = 3200; // 3.2V in mV
  const maxVoltage = 4200; // 4.2V in mV
  const percentage = ((voltage - minVoltage) / (maxVoltage - minVoltage)) * 100;
  return Math.max(0, Math.min(100, percentage));
};

const formatLastSeen = (lastSeen: string): string => {
  const date = new Date(lastSeen);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const MonitoringView: React.FC = () => {
  const api = useAxios();
  const navigate = useNavigate();
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [system, setSystem] = useState<SystemResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const intervalRef = useRef<number | null>(null);
  const gatewayIntervalRef = useRef<number | null>(null);
  const toast = useToast();
  const [gateways, setGateways] = useState<Gateway[]>([]);

  const fetchMonitoringData = async () => {
    try {
      const healthRes = await api.get("monitoring/status/");
      setHealth(healthRes.data);
    } catch (e) {
      console.error(e);
    }

    try {
      const systemRes = await api.get("monitoring/system/");
      setSystem(systemRes.data);
    } catch (e) {
      console.error(e);
    }

    setLastUpdated(new Date().toLocaleTimeString());
  };

  const fetchGatewayData = async () => {
    try {
      const gatewaysRes = await api.get("frontend/gateways/admin/");
      setGateways(gatewaysRes.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchAllData = async () => {
    await Promise.all([fetchMonitoringData(), fetchGatewayData()]);
  };

  useEffect(() => {
    fetchAllData();
    intervalRef.current = window.setInterval(fetchMonitoringData, UPDATE_TIME);
    gatewayIntervalRef.current = window.setInterval(fetchGatewayData, GATEWAY_UPDATE_TIME);
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      if (gatewayIntervalRef.current) window.clearInterval(gatewayIntervalRef.current);
    };
  }, []);

  const cardBg = useColorModeValue("white", "gray.900");

  // Calculate total devices, gateways, and messages from gateway data
  const totalDevices = gateways.reduce((sum, gw) => sum + gw.number_of_devices, 0);
  const totalGateways = gateways.filter(isGatewayOnline).length;
  const totalMessagesLastHour = gateways.reduce(
    (sum, gw) => sum + gw.number_of_messages_last_hour,
    0,
  );

  // Filter and sort gateways for monitoring
  const monitoredGateways = gateways
    .filter((gw) => gw.organization_name || isGatewayOnline(gw))
    .sort((a, b) => {
      // Down gateways first
      const aDown = isGatewayDown(a);
      const bDown = isGatewayDown(b);
      if (aDown && !bDown) return -1;
      if (!aDown && bDown) return 1;

      // Gateways with organization first
      if (a.organization_name && !b.organization_name) return -1;
      if (!a.organization_name && b.organization_name) return 1;

      // Then by last seen (most recent first)
      return new Date(b.last_seen).getTime() - new Date(a.last_seen).getTime();
    });

  return (
    <Navbar>
      <Container maxW="6xl" py={6}>
        <Flex mb={4} align="center">
          <Heading size="2xl">Server Monitoring</Heading>
          <Flex ml="auto" gap={3} align="center">
            <Text fontSize="sm" color="gray.500">
              {lastUpdated ? `Last Updated: ${lastUpdated}` : ""}
            </Text>
            <Tooltip label="Update">
              <IconButton
                aria-label="refresh"
                icon={<RepeatIcon />}
                size="sm"
                onClick={fetchAllData}
              />
            </Tooltip>
          </Flex>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
          <Box bg={cardBg} p={4} rounded="md" boxShadow="md">
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontWeight="bold">Server</Text>
              <Badge colorScheme={statusColor(health?.app || "down")}>
                {health?.app || "down"}
              </Badge>
            </Flex>
            <Text fontSize="sm" color="gray.500">
              {health?.app === "ok"
                ? "The application server is running normally"
                : "The application server is not responding"}
            </Text>
          </Box>

          <Box bg={cardBg} p={4} rounded="md" boxShadow="md">
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontWeight="bold">Database</Text>
              <Badge colorScheme={statusColor(health?.db || "down")}>
                {health?.db?.status || "down"}
              </Badge>
            </Flex>
            {health?.db?.error ? (
              <Text fontSize="xs" color="red.400">
                {health.db.error}
              </Text>
            ) : (
              <Text fontSize="sm" color="gray.500">
                Connection to the database has been established
              </Text>
            )}
          </Box>

          <Box bg={cardBg} p={4} rounded="md" boxShadow="md">
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontWeight="bold">Celery / Broker</Text>
              <Badge colorScheme={statusColor(health?.celery || "down")}>
                {health?.celery?.status || "down"}
              </Badge>
            </Flex>
            {health?.celery?.error ? (
              <Text fontSize="xs" color="red.400">
                {health.celery.error}
              </Text>
            ) : (
              <Text fontSize="sm" color="gray.500">
                {health?.celery?.status === "ok"
                  ? `Workers: ${health.celery.workers?.length || 0}`
                  : "No response from the broker"}
              </Text>
            )}
          </Box>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          <VStack spacing={4} align="stretch">
            <Box bg={cardBg} p={4} rounded="md" boxShadow="md">
              <Flex justify="space-between" mb={2}>
                <Text fontWeight="bold">CPU</Text>
                <Text fontSize="sm" color="gray.500">
                  {system?.cpu?.cores} cores
                </Text>
              </Flex>
              <Progress
                value={system?.cpu?.percent || 0}
                size="lg"
                rounded="md"
                colorScheme={
                  (system?.cpu?.percent || 0) > 80
                    ? "red"
                    : (system?.cpu?.percent || 0) > 60
                      ? "orange"
                      : "green"
                }
                mb={2}
              />
              <Text fontSize="sm" color="gray.600">
                {safeFixed(system?.cpu?.percent, 1)}% loaded
              </Text>
            </Box>

            <Box bg={cardBg} p={4} rounded="md" boxShadow="md">
              <Flex justify="space-between" mb={2}>
                <Text fontWeight="bold">RAM</Text>
                <Text fontSize="sm" color="gray.500">
                  {formatBytes(system?.memory?.used)} / {formatBytes(system?.memory?.total)}
                </Text>
              </Flex>
              <Progress
                value={system?.memory?.percent || 0}
                size="lg"
                rounded="md"
                colorScheme={
                  (system?.memory?.percent || 0) > 85
                    ? "red"
                    : (system?.memory?.percent || 0) > 70
                      ? "orange"
                      : "green"
                }
                mb={2}
              />
              <Text fontSize="sm" color="gray.600">
                Used: {safeFixed(system?.memory?.percent, 1)}%
              </Text>
            </Box>

            <Box bg={cardBg} p={4} rounded="md" boxShadow="md">
              <Flex justify="space-between" mb={2}>
                <Text fontWeight="bold">Disk</Text>
                <Text fontSize="sm" color="gray.500">
                  {system?.disk
                    ? `${formatBytes(system.disk.used)} / ${formatBytes(system.disk.total)}`
                    : "-"}
                </Text>
              </Flex>
              <Progress
                value={system?.disk?.percent || 0}
                size="lg"
                rounded="md"
                colorScheme={
                  (system?.disk?.percent || 0) > 90
                    ? "red"
                    : (system?.disk?.percent || 0) > 75
                      ? "orange"
                      : "green"
                }
                mb={2}
              />
              <Text fontSize="sm" color="gray.600">
                Free Space: {system?.disk ? formatBytes(system.disk.free) : "-"}
              </Text>
            </Box>

            <Box bg={cardBg} p={4} rounded="md" boxShadow="md">
              <Text fontWeight="bold" mb={2}>
                Devices & Gateways
              </Text>
              <SimpleGrid columns={3} spacing={3}>
                <Stat>
                  <StatLabel>Devices</StatLabel>
                  <StatNumber>{totalDevices}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Gateways</StatLabel>
                  <StatNumber>{totalGateways}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Messages/hour</StatLabel>
                  <StatNumber>{totalMessagesLastHour}</StatNumber>
                </Stat>
              </SimpleGrid>
            </Box>
          </VStack>

          <VStack spacing={4} align="stretch">
            <Box bg={cardBg} p={4} rounded="md" boxShadow="md">
              <Text fontWeight="bold" mb={2}>
                Load average
              </Text>
              {system?.loadavg ? (
                <HStack spacing={4}>
                  <Stat>
                    <StatLabel>1 min</StatLabel>
                    <StatNumber>{safeFixed(system.loadavg.short, 2)}</StatNumber>
                    <StatHelpText>last 1 min</StatHelpText>
                  </Stat>
                  <Stat>
                    <StatLabel>5 min</StatLabel>
                    <StatNumber>{safeFixed(system.loadavg.medium, 2)}</StatNumber>
                    <StatHelpText>medium</StatHelpText>
                  </Stat>
                  <Stat>
                    <StatLabel>15 min</StatLabel>
                    <StatNumber>{safeFixed(system.loadavg.long, 2)}</StatNumber>
                    <StatHelpText>long load</StatHelpText>
                  </Stat>
                </HStack>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  No data
                </Text>
              )}
            </Box>

            <Box bg={cardBg} p={4} rounded="md" boxShadow="md">
              <Text fontWeight="bold" mb={2}>
                debug
              </Text>
              <Box
                as="pre"
                fontSize="xs"
                bg={useColorModeValue("gray.100", "gray.800")}
                p={3}
                rounded="md"
                overflowX="auto"
                maxH="200px"
              >
                {JSON.stringify({ health, system }, null, 2)}
              </Box>
            </Box>
          </VStack>
        </SimpleGrid>

        {/* Gateway Monitoring Section */}
        <Box mt={6}>
          <Heading size="lg" mb={4}>
            Gateway Status
          </Heading>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
            {monitoredGateways.map((gateway) => {
              const isDown = isGatewayDown(gateway);
              const batteryPercent = getBatteryPercentage(gateway.gateway_battery_voltage);

              return (
                <Box
                  key={gateway.id}
                  bg={cardBg}
                  p={4}
                  rounded="md"
                  boxShadow="md"
                  borderLeft="4px solid"
                  borderColor={
                    isDown ? "red.500" : isGatewayOnline(gateway) ? "green.500" : "gray.500"
                  }
                >
                  <Flex justify="space-between" align="center" mb={2}>
                    <HStack spacing={3}>
                      <Badge
                        colorScheme={isDown ? "red" : isGatewayOnline(gateway) ? "green" : "gray"}
                      >
                        {isDown ? "DOWN" : isGatewayOnline(gateway) ? "ONLINE" : "OFFLINE"}
                      </Badge>
                      <Text fontWeight="bold" fontSize="lg">
                        {gateway.gateway_name || gateway.gateway_address}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {gateway.organization_name}
                      </Text>
                      <IconButton
                        aria-label="Details"
                        icon={<InfoIcon />}
                        size="xs"
                        variant="ghost"
                        onClick={() => navigate(`/monitoring/gateway/${gateway.id}`)}
                      />
                    </HStack>
                    <Text fontSize="sm" color="gray.500">
                      {formatLastSeen(gateway.last_seen)}
                    </Text>
                  </Flex>

                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Devices
                      </Text>
                      <Text fontSize="lg" fontWeight="bold">
                        {gateway.number_of_devices}
                      </Text>
                    </Box>

                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Messages/hour
                      </Text>
                      <Text fontSize="lg" fontWeight="bold">
                        {gateway.number_of_messages_last_hour}
                      </Text>
                    </Box>

                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Battery
                      </Text>
                      {batteryPercent !== null ? (
                        <HStack spacing={2}>
                          <Text fontSize="lg" fontWeight="bold">
                            {batteryPercent.toFixed(0)}%
                          </Text>
                          <Progress
                            value={batteryPercent}
                            size="sm"
                            rounded="md"
                            colorScheme={
                              batteryPercent > 50 ? "green" : batteryPercent > 20 ? "orange" : "red"
                            }
                            width="60px"
                          />
                        </HStack>
                      ) : (
                        <Text fontSize="lg" fontWeight="bold" color="gray.400">
                          N/A
                        </Text>
                      )}
                    </Box>

                    <Box>
                      <Text fontSize="xs" color="gray.500">
                        Address
                      </Text>
                      <Text fontSize="sm" fontFamily="mono">
                        {gateway.gateway_address}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>
              );
            })}

            {monitoredGateways.length === 0 && (
              <Box bg={cardBg} p={6} rounded="md" boxShadow="md" textAlign="center">
                <Text color="gray.500">No gateways to monitor</Text>
              </Box>
            )}
          </SimpleGrid>
        </Box>
      </Container>
    </Navbar>
  );
};

export default MonitoringView;
